import { useSelector, useDispatch } from 'react-redux';
import { Skeleton } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  selectEnrollmentContext,
  selectEnrollmentOrgPlanYearId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import {
  selectEnrollmentLatest,
  setEnrollmentLatest,
} from 'enrollment/components/BottomNavigation/services';
import {
  useConfigurationQuery,
  useEnrollmentContextQuery,
  useSubscriberQuery,
  BenefitCategory,
} from 'theSource/queries';
import { ROUTES } from 'enrollment/app/routes';
import { StyledIframe, StyledText } from './BundledBenefitsStyled';
import { InternalPage } from 'theSource/components';
import { BottomNavigation } from 'enrollment/components';
import { PsModalTuner } from 'stencilSetup';
import { queryClient, queryKeyGenerator } from 'theSource/utils';
import { useEnrollmentStatusQuery } from 'enrollment/queries';
import { benefitCategorySelector } from 'enrollment/services/BenefitCategories/selectors';
import { useBundledBenefitCategories } from 'enrollment/utils/bundledBenefitsUtils';
import { setBenefitCategoryData } from 'enrollment/services/BenefitCategories/slice';

declare const bootstrap: any;

const savviAllowList = [
  'https://selectsmart.savvifi.dev',
  'https://selectsmart.savvifi.com',
  'https://enrollment.dev.plansourceds.com',
  'https://enrollment.tst.plansourceds.com',
  'https://enrollment.stg.plansourceds.com',
  'https://enrollment.plansourceds.com',
  'https://api.dev.plansourceds.com',
  'https://api.tst.plansourceds.com',
  'https://api.stg.plansourceds.com',
  'https://api.plansourceds.com',
];

export const BundledBenefits = () => {
  const { t } = useTranslation('pages.bundledBenefits');
  const navigate = useNavigate();
  const { configuration, isLoadingConfiguration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;
  const enrollmentLatest = useSelector(selectEnrollmentLatest);
  const benefitCategories = useSelector(benefitCategorySelector);
  const savviCategories: BenefitCategory[] = useBundledBenefitCategories();
  const dispatch = useDispatch();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const openPopup = () => {
    const planPopup = new bootstrap.Modal(
      document.getElementById('skip-bundled-benefits-popup') as HTMLElement
    );
    planPopup && planPopup.show();
  };

  const { EnrollmentStatusRequest } = useEnrollmentStatusQuery({
    lumosApiHost: String(lumosApiHost),
  });

  const onClickSkipBundledBenefits = () => {
    if (enrollmentLatest === 'decision_support') {
      EnrollmentStatusRequest({
        enrollment_type: enrollmentContext,
        status: String(benefitCategories[0].lookup_code),
      });
      dispatch(setEnrollmentLatest(String(benefitCategories[0].lookup_code)));
    }
    navigate(`/enrollment${ROUTES.enrollment.benefits.path}`);
  };

  const onSuccessfulCheckout = () => {
    dispatch(setBenefitCategoryData({ benefitsCategory: savviCategories }));
    queryClient.refetchQueries(
      queryKeyGenerator(`benefits-${enrollmentContext}`) // don't need org plan year context because this doesn't run for life events
    );

    navigate(
      `/enrollment/${ROUTES.enrollment.decisionSupportRouter.path}/complete`
    );
  };

  const enrollmentContext = useSelector(selectEnrollmentContext);
  const orgPlanYearID = useSelector(selectEnrollmentOrgPlanYearId);
  const [loader, setLoaderView] = useState(true);

  const { enrollmentContext: enrollmentContextResponse } =
    useEnrollmentContextQuery({
      enabled: true,
      apiHost: String(lumosApiHost),
    });
  const { subscriber, isLoadingSubscriber, isFetchingSubscriber } =
    useSubscriberQuery();

  let opyID = orgPlanYearID;
  if (!opyID || !(opyID > 0)) {
    const context = enrollmentContextResponse?.data?.find(
      (context) => context.enrollment_type_lookup_code === enrollmentContext
    );
    if (context) {
      opyID = context.org_plan_year_id;
    }
  }
  let savviUrl: string | undefined = undefined;
  if (
    lumosApiHost &&
    opyID &&
    opyID > 0 &&
    ['life_event', 'initial', 'open'].includes(enrollmentContext) &&
    subscriber?.data.id.value &&
    typeof subscriber?.data.id.value === 'number'
  ) {
    const mappedEnrollmentContext =
      enrollmentContext === 'open' ? 'annual' : enrollmentContext;
    savviUrl = `${lumosApiHost}/sso/subscriber_identity_provider/send_to_service_provider?provider=savvi&enrollment_context_type=${mappedEnrollmentContext}&plan_year_id=${opyID}&subscriber_id=${subscriber?.data.id.value}`;
  }

  const resizeIframe = (height: number) => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    iframe.style.height = `${height}px`;
  };

  // Listen for messages from the iframe for the savvi origin only
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!savviAllowList.includes(event.origin)) {
        return; // invalid origin
      }

      if (
        event.data &&
        typeof event.data === 'object' &&
        'guidance' in event.data
      ) {
        // scroll to top when iframe goes to next page
        if (
          event.data.guidance.navigate &&
          event.data.guidance.navigate === true
        ) {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
        // dynamically resize iframe
        if (
          event.data.guidance.contentHeight &&
          typeof event.data.guidance.contentHeight === 'number'
        ) {
          const height = event.data.guidance.contentHeight as number;
          resizeIframe(height);
        }
        // if savvi sends a message to exit the bundled benefits flow
        // check if success or error
        if (event.data.guidance.exit && event.data.guidance.exit === true) {
          if (event.data.guidance.reason === 'completed') {
            onSuccessfulCheckout();
          } else {
            onClickSkipBundledBenefits();
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  });

  if (
    isLoadingSubscriber ||
    isFetchingSubscriber ||
    isLoadingConfiguration ||
    !savviUrl
  ) {
    return (
      <div>
        <Skeleton
          variant="rectangular"
          height="400px"
          width="100%"
          data-testid="BundledBenefits-loading-page"
          role="status"
          aria-label="Loading..."
        />
      </div>
    );
  }

  return (
    <InternalPage
      testId="savvi-bundled-benefits-iframe-page"
      blockClasses="mt-md-5 mt-lg-0 pt-3 pb-4 pt-md-5 p-lg-0"
    >
      {loader ? (
        <Skeleton
          variant="rectangular"
          height="600px"
          width="100%"
          data-testid="BundledBenefits-loading-page"
          role="status"
          aria-label="Loading..."
        />
      ) : (
        <></>
      )}
      <StyledIframe
        data-testid="BundledBenefits-iframe"
        ref={iframeRef}
        src={savviUrl}
        width="100%"
        height="0px"
        title={t('title')}
        allow={`frame-ancestors 'none'; geolocation 'none'; fullscreen 'none'; frame-src 'self' ${savviAllowList.toString().replaceAll(',', ' ')} https://*.plansource.com https://*.plansourcestage.com`}
        referrerPolicy="strict-origin-when-cross-origin"
        onError={onClickSkipBundledBenefits}
        onLoad={() => {
          setLoaderView(false);
        }}
      />
      <BottomNavigation
        onClickEvent={() => openPopup()}
        altContinueBtnText={t('customizeButton')}
        buttonType="secondary"
        skipStatusUpdater={true}
      >
        <StyledText>{t('customize')}</StyledText>
      </BottomNavigation>
      <PsModalTuner
        modalId="skip-bundled-benefits-popup"
        data-testid="skip-bundled-benefits-popup"
        modalTitle={t('popup.title')}
        primaryActive
        primaryButton={t('common:continue')}
        secondaryButton={t('common:cancel')}
        onPsModalSubmit={() => onClickSkipBundledBenefits()}
      >
        <div>
          <p>{t('popup.description1')}</p>
          <p>{t('popup.description2')}</p>
        </div>
      </PsModalTuner>
    </InternalPage>
  );
};
